import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTrophy, FaChartLine, FaUsers, FaCalendarAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { teams } from '../data/Interclubs';
import PageHeader from '../components/PageHeader';
import ScrollReveal, { StaggerReveal, StaggerItem } from '../components/ScrollReveal';

const TeamCard = ({ name, division, image, results, onDetailsClick }) => (
  <StaggerItem>
    <div className="card-minimal overflow-hidden group cursor-pointer hover:-translate-y-0.5 transition-transform duration-300" onClick={onDetailsClick}>
      <div className="relative h-52 overflow-hidden">
        <img src={image} alt={name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <h3 className="text-base font-bold text-white">{name}</h3>
          <p className="text-white/60 text-xs">{division}</p>
        </div>
      </div>
      <div className="p-5 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-stone-400 mb-0.5">Classement</p>
          <p className="text-stone-800 font-semibold text-sm">{results.currentRank}</p>
        </div>
        <span className="btn-ghost text-xs py-1.5 px-4">Détails</span>
      </div>
    </div>
  </StaggerItem>
);

const TeamModal = ({ team, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    onClick={onClose}
    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
  >
    <motion.div
      initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={e => e.stopPropagation()}
      className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden"
    >
      <div className="relative h-52">
        <img src={team.image} alt={team.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h2 className="text-xl font-bold text-white">{team.name}</h2>
          <p className="text-white/60 text-sm">{team.division}</p>
        </div>
      </div>
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div className="card-minimal p-4">
            <h3 className="font-semibold text-stone-800 text-xs mb-2 flex items-center gap-2">
              <FaTrophy className="text-amber-500" size={12} /> Palmarès
            </h3>
            <ul className="space-y-1">
              {team.results.achievements.map((a, i) => (
                <li key={i} className="text-stone-500 text-xs flex gap-2">
                  <span className="text-brand-800">•</span>{a}
                </li>
              ))}
            </ul>
          </div>
          <div className="card-minimal p-4">
            <h3 className="font-semibold text-stone-800 text-xs mb-2 flex items-center gap-2">
              <FaChartLine className="text-blue-500" size={12} /> Résultats
            </h3>
            <div className="space-y-1 text-xs text-stone-500">
              <p>Classement : <strong className="text-stone-800">{team.results.currentRank}</strong></p>
              <p>Victoires : <strong className="text-stone-800">{team.results.wins}</strong></p>
              <p>Défaites : <strong className="text-stone-800">{team.results.losses}</strong></p>
            </div>
          </div>
        </div>
        <div>
          <h3 className="font-semibold text-stone-800 text-xs mb-2 flex items-center gap-2">
            <FaUsers className="text-green-500" size={12} /> Composition
          </h3>
          <div className="flex flex-wrap gap-2">
            {team.players.map((p, i) => (
              <span key={i} className="tag tag-subtle">{p}</span>
            ))}
          </div>
        </div>
        <button onClick={onClose} className="btn-brand w-full justify-center">Fermer</button>
      </div>
    </motion.div>
  </motion.div>
);

const Interclub = ({ setCurrentSection, currentSection }) => {
  const [selectedTeam, setSelectedTeam] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); }, [currentSection]);

  return (
    <>
      <PageHeader
        title="Nos Équipes Interclub"
        subtitle="Découvrez nos équipes et leurs performances en compétition"
        setCurrentSection={setCurrentSection}
        currentSection={currentSection}
      />

      <section className="section bg-stone-50">
        <div className="max-w-6xl mx-auto space-y-12">
          <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teams.map((team, i) => (
              <TeamCard key={i} {...team} onDetailsClick={() => setSelectedTeam(team)} />
            ))}
          </StaggerReveal>

          <ScrollReveal>
            <div className="card-minimal p-10 text-center">
              <div className="w-12 h-12 rounded-2xl bg-brand-50 flex items-center justify-center text-brand-800 mx-auto mb-5">
                <FaCalendarAlt size={20} />
              </div>
              <h2 className="font-bold text-stone-950 text-lg mb-2">Calendrier des prochains matchs</h2>
              <p className="text-stone-400 text-sm mb-6 max-w-sm mx-auto leading-relaxed">
                Retrouvez le calendrier complet et venez soutenir nos équipes !
              </p>
              <Link to="/agenda" onClick={() => setCurrentSection('agenda')} className="btn-brand">
                Voir le calendrier
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <AnimatePresence>
        {selectedTeam && <TeamModal team={selectedTeam} onClose={() => setSelectedTeam(null)} />}
      </AnimatePresence>
    </>
  );
};

export default Interclub;
